import { useCallback, useMemo } from "react";
import { FUNNEL_TYPES } from "../utils/contants";
import { Flex, Spinner } from "@chakra-ui/react";
import {
  FunnelConfigTypes,
  FunnelContextTypes,
  FunnelOnSuccess,
  FunnelOptionsTypes,
} from "../types";
import { useFunnelNavigationContext } from "./useFunnelNavigationContext";
import { useFlowIndicators } from "./useFlowIndicators";

export interface BuildFunnelConfig {
  type?: FUNNEL_TYPES;
  context?: FunnelContextTypes;
  options?: FunnelOptionsTypes;
  props: {
    setFunnelType: (type: FUNNEL_TYPES) => void;
    updateFunnelConfig: (config: FunnelConfigTypes) => void;
    onClose: () => void;
    onSuccess: FunnelOnSuccess;
    sendEvent?: (event: any) => void;
    shouldShowSteps?: boolean;
    stepCount?: number;
    currentStep?: number;
    stepText?: string;
    stepDisplayText?: string;
    showStepIndicators?: boolean;
  };
}

interface FunnelContent {
  funnelHeader?: JSX.Element;
  funnelBody?: JSX.Element;
  funnelFooter?: JSX.Element;
}

type BuildFunnel = (config: BuildFunnelConfig) => FunnelContent;

export interface UseBuildFunnelParams {
  setFunnelConfig: (config?: FunnelConfigTypes) => void;
  onCloseFunnel?: () => void;
  openFunnelType?: FUNNEL_TYPES;
  context?: FunnelContextTypes;
  options?: FunnelOptionsTypes;
}

export function useBuildFunnel(
  buildFunnel: BuildFunnel,
  params: UseBuildFunnelParams,
) {
  const { setFunnelConfig, onCloseFunnel, openFunnelType, context, options } =
    params;
  // Handle the closing of a funnel
  // If the funnel is locked, do not close
  const onClose = useCallback(() => {
    setFunnelConfig();

    if (!options?.locked) onCloseFunnel?.();
    options?.onClose?.();
  }, [setFunnelConfig, options?.locked, onCloseFunnel, options?.onClose]);

  const onSuccess: FunnelOnSuccess = useCallback(
    (result) => {
      // Auto-close modal and call parent callback
      setFunnelConfig();

      if (options?.onSuccess) {
        options.onSuccess({
          ...result,
          lastFunnelType: openFunnelType,
          context: {
            ...context,
            ...result.context,
          },
          options: {
            ...options,
            ...result.options,
          },
        });
      } else {
        onCloseFunnel?.();
      }
    },
    [setFunnelConfig, options, openFunnelType, context, onCloseFunnel],
  );

  const updateFunnelConfig = useCallback(
    (config: FunnelConfigTypes) => {
      setFunnelConfig({
        ...config,
        context: {
          ...context,
          ...config.context,
        },
        options: {
          ...options,
          ...config.options,
        },
      });
    },
    [setFunnelConfig, context, options],
  );

  const setFunnelType = useCallback(
    (funnelType: FUNNEL_TYPES) => {
      setFunnelConfig({
        type: funnelType,
        context,
        options,
      });
    },
    [setFunnelConfig, context, options],
  );

  const sendEvent = useFunnelNavigationContext(
    openFunnelType,
    context?.navigationContext,
  );

  const stepConfig = useFlowIndicators({
    authMethod: options?.authMethod,
    didPurchaseSubscription: options?.didPurchaseSubscription,
    funnelType: openFunnelType,
    modalContext: context?.modalContext,
    release: context?.purchaseRelease,
  });

  return useMemo(() => {
    if (!openFunnelType) {
      return {
        onClose,
        funnelHeader: undefined,
        funnelBody: undefined,
        funnelFooter: undefined,
      };
    }

    const {
      hasContent,
      stepCount,
      currentStep,
      stepText,
      text,
      showStepIndicators,
    } = stepConfig;

    const { funnelHeader, funnelBody, funnelFooter } = buildFunnel({
      type: openFunnelType,
      context,
      options,
      props: {
        onSuccess,
        onClose,
        sendEvent,
        setFunnelType,
        updateFunnelConfig,
        shouldShowSteps: hasContent,
        stepCount,
        currentStep,
        stepText,
        stepDisplayText: text,
        showStepIndicators,
      },
    });

    return {
      funnelHeader,
      funnelBody: funnelBody ?? (
        <Flex width="100%" justifyContent="center">
          <Spinner color="blaze.blaze" />
        </Flex>
      ),
      funnelFooter,
      onClose,
    };
  }, [
    openFunnelType,
    options,
    context,
    stepConfig,
    onSuccess,
    onClose,
    sendEvent,
    setFunnelType,
    updateFunnelConfig,
    buildFunnel,
  ]);
}
